import { Tooltip } from "@progress/kendo-react-tooltip";
import { useMemo } from "react";
import type { ScenarioLibraryScenario } from "../../../../models/scenarioLibraryScenario";
import type { CommercialPlanMetrics } from "../../../../utils/commercialPlanCalculations";
import { formatCurrency, formatPercentage, isDefinedNumber } from "../../../../utils/numberValueConversion";
import { getScenarioInformation } from "./scenarioInformation";

type ScenarioMetricsComparisonGridProps = {
    selectedScenarios: (ScenarioLibraryScenario | null)[];
};

type MetricRow = {
    label: string;
    amount: (metrics: CommercialPlanMetrics) => number | null;
    percentage: (metrics: CommercialPlanMetrics) => number | null;
};

const metricRows: MetricRow[] = [
    {
        label: "Revenue",
        amount: metrics => metrics.projectedRevenue,
        percentage: metrics => metrics.revenuePercentageChange
    },
    {
        label: "Profit",
        amount: metrics => metrics.projectedProfit,
        percentage: metrics => metrics.profitPercentageChange
    },
    {
        label: "ROI",
        amount: () => null,
        percentage: metrics => metrics.roi
    }
];

const formatSignedPercentage = (value: number | null): string => {
    if (!isDefinedNumber(value)) {
        return "-";
    }

    return `${value > 0 ? "+" : ""}${formatPercentage(value)}`;
};

const getResultBadgeClassName = (value: number): string => {
    if (value === 0) {
        return "bg-secondary-subtle text-secondary-emphasis";
    }

    return value > 0
        ? "bg-success-subtle text-success-emphasis"
        : "bg-danger-subtle text-danger-emphasis";
};

const ScenarioMetricsComparisonGrid: React.FC<ScenarioMetricsComparisonGridProps> = ({ selectedScenarios }) => {
    const scenarioMetrics = useMemo(
        () => selectedScenarios.map(scenario => scenario ? getScenarioInformation(scenario.original).metrics : null),
        [selectedScenarios]
    );

    return (
        <div className="border rounded-4 overflow-hidden bg-body flex-shrink-0" aria-label="Scenario metrics comparison">
            <div className="table-responsive">
                <table className="table table-fixed align-middle mb-0" style={{ tableLayout: "fixed", minWidth: "640px" }}>
                    <colgroup>
                        <col style={{ width: "20%" }} />
                        {selectedScenarios.map((_, index) => <col key={index} style={{ width: `${80 / 3}%` }} />)}
                    </colgroup>
                    <thead className="table-light">
                        <tr>
                            <th className="px-3 py-3 text-start" scope="col">Metric</th>
                            {selectedScenarios.map((scenario, index) => (
                                <th
                                    className="px-3 py-3 text-end text-body"
                                    key={index}
                                    scope="col"
                                    style={{ whiteSpace: "normal", overflowWrap: "anywhere" }}
                                >
                                    {scenario
                                        ? (
                                                <Tooltip anchorElement="target" position="top">
                                                    <span title={scenario.title}>
                                                        Scenario
                                                        {" "}
                                                        {String.fromCharCode(65 + index)}
                                                    </span>
                                                </Tooltip>
                                            )
                                        : (
                                                <span>
                                                    Scenario
                                                    {" "}
                                                    {String.fromCharCode(65 + index)}
                                                </span>
                                            )}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {metricRows.map(row => (
                            <tr key={row.label}>
                                <th className="px-3 py-3 text-start fw-normal text-body-secondary" scope="row">{row.label}</th>
                                {scenarioMetrics.map((metrics, index) => {
                                    const percentage = metrics ? row.percentage(metrics) : null;
                                    const amount = metrics ? row.amount(metrics) : null;
                                    const isAmountMetric = row.label !== "ROI";
                                    const isAvailable = isDefinedNumber(percentage)
                                        && (!isAmountMetric || isDefinedNumber(amount));

                                    return (
                                        <td className="px-3 py-3 text-end" key={index}>
                                            {isAvailable
                                                ? (
                                                        <span
                                                            className={`badge d-inline-flex flex-wrap justify-content-end align-items-center rounded-3 px-3 py-2 text-wrap ${getResultBadgeClassName(percentage)}`}
                                                            style={{ fontSize: "0.8rem", lineHeight: 1.25, maxWidth: "100%" }}
                                                        >
                                                            {isAmountMetric && (
                                                                <span className="fw-bold">
                                                                    {formatCurrency(amount, 0)}
                                                                </span>
                                                            )}
                                                            <span className={isAmountMetric ? "border-start border-current ms-2 ps-2 fw-semibold" : "fw-semibold"}>
                                                                {formatSignedPercentage(percentage)}
                                                            </span>
                                                        </span>
                                                    )
                                                : (
                                                        <span className="text-body-secondary">-</span>
                                                    )}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ScenarioMetricsComparisonGrid;
